import React, { useEffect, useState } from 'react';

import moment from 'moment';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { useTheme } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import MenuItem from '@material-ui/core/MenuItem';
import TablePagination from '@material-ui/core/TablePagination';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import {
  EMPLOYEE_STATUS,
  EmployeeStatus,
  loadOrgEmployeesByStatus,
} from './actions/load-org-employees-by-status';
import { openOrCloseGlobalDialog } from '../action/dialog/global-dialog';
import { updateHasDismissedImportEmployeeListMessage } from '../action/display-settings/update-has-dismissed-import-employee-list-message';
import { loadEmployeeGroups } from '../employee-groups/actions/load-employee-groups';
import { employeeGroupsForOrgIdSelector } from '../employee-groups/employee-group-selectors';
import { loadHrisIntegrationResponse } from '../hris-integration-response/actions/load-hris-integration-response';
import { hrisIntegrationResponseForOrgIdSelector } from '../hris-integration-response/hris-integration-response-selectors';
import { loadMergeIntegration } from '../merge-integration/actions/load-merge-integration';
import { mergeIntegrationForOrgIdSelector } from '../merge-integration/merge-integration-selectors';
import { loadOrg } from '../organization/actions/load-org';
import { userSelector } from '../reducers/current-user-selectors';
import { hasDismissedImportEmployeeListMessageSelector } from '../reducers/display-settings-selectors';
import {
  employeesForOrgIdSelector,
  employeesStateForOrgIdAndAllStatusSelector,
} from '../reducers/employee-selectors';
import { orgSelector } from '../reducers/org-selectors';
import { RequestState } from '../reducers/utils/state-manager';

import { GLOBAL_DIALOGS } from '../dialogs/global-dialogs-config';
import { FeatureFlagged, FeatureGatedVariant } from '../feature-gates/feature-flagged';
import { FeatureGates } from '../feature-gates/feature-gates';
import { PayrollIntegrationButton } from '../merge-integration/payroll-integration-button';
import { ReactComponent as QuickbooksIcon } from '../quickbooks/qb-icon.svg';
import { UiBreadcrumbHeader } from '../ui/ui-breadcrumb-header';
import { UiFilterSelect } from '../ui/ui-filter-select';
import { UiFilterTextField } from '../ui/ui-filter-text-field';
import { UiKebabMenu } from '../ui/ui-kebab-menu';
import { UiMessageBanner } from '../ui/ui-message-banner';
import { UiSortLabel } from '../ui/ui-sort-label';
import { employeeGroupsRoute, employeeMessagesRoute, employeesRoute } from '../web-routes';

import { EmployeeEmptyState } from './employee-empty-state';
import { EmployeeListUploadDialog } from './employee-list-upload-dialog';
import { employeeStatusText } from './employee-status';
import { EmployeeSummaryCard } from './employee-summary-card';
import { EmployeeProfileDialog } from './profile/employee-profile-dialog';

import { employeeName } from './utils/employee-name';
import { orgHasNotAddedEmployees } from './utils/org-has-not-added-employees';
import { REHIRE_ELIGIBLE_TYPES } from './utils/rehire-eligible-types';
import { csvExporter } from '../util/csv-exporter';
import { combineWords, formatPhoneNumber } from '../util/formatters';
import { isTruthy } from '../util/is-truthy';
import { LANGUAGES } from '../util/languages';
import { HeaderButtonAddIcon } from '../util/material-design-icons';
import { EMPLOYEE_PERMISSION } from '../util/permissions';
import { SORT_ORDER, sortAlphabetically } from '../util/sort-functions';
import { error } from '../widgets/error-message-dialog';
import Message from '../widgets/legacy-message';
import { LoadingMessage } from '../widgets/loading-message';

const mapStateToProps = (state: any, { orgId }: InputProps) => {
  const user = userSelector(state);
  const employeesAreLoading = employeesStateForOrgIdAndAllStatusSelector(state, orgId).every(
    (requestState: RequestState) => !requestState || requestState.loading,
  );

  return {
    employees: employeesForOrgIdSelector(state, orgId),
    employeesAreLoading,
    org: orgSelector(state, orgId),
    employeeGroups: employeeGroupsForOrgIdSelector(state, orgId),
    user,
    hasDismissedImportEmployeeListMessage:
      user.username && hasDismissedImportEmployeeListMessageSelector(state, user.username),
    mergeIntegration: mergeIntegrationForOrgIdSelector(state, orgId),
    hrisIntegrationResponse: hrisIntegrationResponseForOrgIdSelector(state, orgId),
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    loadOrg: () => dispatch(loadOrg(orgId)),
    loadOrgEmployeesByStatus: (employeeStatus: EmployeeStatus) =>
      dispatch(loadOrgEmployeesByStatus(orgId, employeeStatus)),
    loadOrgEmployeeGroups: () => dispatch(loadEmployeeGroups(orgId)),
    toggleAddEmployeeDialog: (isOpen: boolean, onClose?: () => void) =>
      dispatch(
        openOrCloseGlobalDialog(GLOBAL_DIALOGS.ADD_EMPLOYEE_DIALOG.key, isOpen, { orgId, onClose }),
      ),
    dismissImportEmployeeListMessage: (user: AuthenticatedUser) =>
      user.username && dispatch(updateHasDismissedImportEmployeeListMessage(user.username, true)),
    loadMergeIntegration: () => dispatch(loadMergeIntegration(orgId)),
    loadHrisIntegrationResponse: () => dispatch(loadHrisIntegrationResponse(orgId)),
  },
});

export const issueFilters = {
  optedOutOfTexts: {
    label: 'Opted Out of Texts',
    message: 'opted out of texts',
    filter: (employees: Employee[]) => employees.filter((e) => e.optedOutOfTexts),
    link: (orgId: number) => employeesRoute(orgId, { filterByIssue: 'optedOutOfTexts' }),
  },
  missingContactInfo: {
    label: 'Missing Email and Phone',
    message: 'no email or phone number',
    filter: (employees: Employee[]) =>
      employees.filter((e) => !e.Contact.Person.email && !e.Contact.Person.phoneNumber),
    link: (orgId: number) => employeesRoute(orgId, { filterByIssue: 'missingContactInfo' }),
  },
  missingEmail: {
    label: 'Missing Email',
    message: 'no email',
    filter: (employees: Employee[]) => employees.filter((e) => !e.Contact.Person.email),
    link: (orgId: number) => employeesRoute(orgId, { filterByIssue: 'missingEmail' }),
  },
  missingPhoneNumber: {
    label: 'Missing Phone Number',
    message: 'no phone number',
    filter: (employees: Employee[]) => employees.filter((e) => !e.Contact.Person.phoneNumber),
    link: (orgId: number) => employeesRoute(orgId, { filterByIssue: 'missingPhoneNumber' }),
  },
  invalidPhoneNumber: {
    label: 'Invalid Phone Numbers',
    message: 'invalid phone numbers',
    filter: (employees: Employee[]) =>
      employees.filter((e) => e.Contact.Person.isPhoneNumberInvalid),
    link: (orgId: number) => employeesRoute(orgId, { filterByIssue: 'invalidPhoneNumber' }),
  },
  missingSupervisor: {
    label: 'Missing Supervisor',
    message: 'no supervisor',
    filter: (employees: Employee[]) =>
      employees.filter((e) => e.Supervisors.length === 0 && !e.hasNoSupervisor),
    link: (orgId: number) => employeesRoute(orgId, { filterByIssue: 'missingSupervisor' }),
  },
  missingBirthdate: {
    label: 'Missing Birthdate',
    message: 'no birthdate',
    filter: (employees: Employee[]) => employees.filter((e) => !e.Contact.Person.birthdate),
    link: (orgId: number) => employeesRoute(orgId, { filterByIssue: 'missingBirthdate' }),
  },
  missingStartDate: {
    label: 'Missing Start Date',
    message: 'no start date',
    filter: (employees: Employee[]) => employees.filter((e) => !e.startDate),
    link: (orgId: number) => employeesRoute(orgId, { filterByIssue: 'missingStartDate' }),
  },
};

const possibleIssues = Object.keys(issueFilters) as Array<keyof typeof issueFilters>;
const ISSUE_OPTIONS = possibleIssues.map((issue) => ({
  label: issueFilters[issue].label,
  value: issue,
}));

const isFutureStartDate = (employee: Employee) =>
  employee.startDate && moment(employee.startDate).isAfter(moment());

const employmentStatusFilters = {
  allEmployees: {
    label: 'All Employees',
    filter: (employees: Employee[]) => employees,
  },
  currentEmployees: {
    label: 'Current Employees',
    filter: (employees: Employee[]) =>
      employees.filter((e) => e.isCurrentEmployee && !isFutureStartDate(e)),
  },
  futureEmployees: {
    label: 'Future Employees',
    filter: (employees: Employee[]) =>
      employees.filter((e) => e.isCurrentEmployee && isFutureStartDate(e)),
  },
  rehireEligible: {
    label: 'Rehire Eligible',
    filter: (employees: Employee[]) =>
      employees.filter(
        (e) => !e.isCurrentEmployee && e.rehireEligible === REHIRE_ELIGIBLE_TYPES.YES,
      ),
  },
};
const possibleStatusFilters = Object.keys(employmentStatusFilters) as Array<
  keyof typeof employmentStatusFilters
>;
const STATUS_OPTIONS = possibleStatusFilters.map((status) => ({
  label: employmentStatusFilters[status].label,
  value: status,
}));

type InputProps = {
  orgId: number;
  queryParams: {
    filterByIssue?: keyof typeof issueFilters;
    filterByName?: string;
    filterByGroup?: string;
    filterByStatus?: string;
    sortBy?: 'firstName' | 'lastName' | 'status';
    sortDirection?: SortDirection;
  };
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const EmployeeList = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: Props) => {
  const {
    employees,
    employeeGroups,
    employeesAreLoading,
    mergeIntegration,
    hrisIntegrationResponse,
    org,
    orgId,
    user,
    hasDismissedImportEmployeeListMessage,
    queryParams,
    actions,
  } = props;

  const theme = useTheme();
  const isOnMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const isOnSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [uploadDialogOpen, setUploadDialogOpen] = useState<boolean>(false);
  const [viewEmployeeDetailId, setViewEmployeeDetailId] = useState<number | null>(null);
  const [selectedTab, setSelectedTab] = useState<string | null>(null);
  const [filterByName, setFilterByName] = useState<string>(queryParams?.filterByName || '');
  const pageSize = 20;
  const [offset, setOffset] = useState<number>(0);

  const filterByGroup = queryParams.filterByGroup || '';
  const filterByIssue = queryParams.filterByIssue || '';
  const filterByStatus = queryParams.filterByStatus || 'currentEmployees';
  const showFormerEmployees = filterByStatus === 'allEmployees';
  const sortBy = queryParams.sortBy || 'lastName';
  const sortDirection = queryParams.sortDirection || 'asc';

  const history = useHistory();

  useEffect(() => {
    actions.loadOrg();
    actions.loadOrgEmployeeGroups();
    if (user.isAccountAdmin) {
      actions.loadMergeIntegration();
      actions.loadHrisIntegrationResponse();
    }
  }, [actions, user.isAccountAdmin]);

  useEffect(() => {
    if (filterByStatus === 'currentEmployees' || filterByStatus === 'futureEmployees') {
      actions.loadOrgEmployeesByStatus(EMPLOYEE_STATUS.CURRENT);
    } else if (filterByStatus === 'rehireEligible') {
      actions.loadOrgEmployeesByStatus(EMPLOYEE_STATUS.FORMER);
    } else {
      actions.loadOrgEmployeesByStatus(EMPLOYEE_STATUS.CURRENT);
      actions.loadOrgEmployeesByStatus(EMPLOYEE_STATUS.FORMER);
    }
  }, [actions, filterByStatus]);

  const message = (employeeId: number) => () => {
    if (org?.org_id) {
      history.push(employeeMessagesRoute({ orgId: org.org_id, employeeId }));
    }
  };

  const viewDetail = (employeeId: number, tab: string) => () => {
    setViewEmployeeDetailId(employeeId);
    setSelectedTab(tab);
  };

  const closeDetailsDialog = () => {
    setViewEmployeeDetailId(null);
    setSelectedTab(null);
  };

  const openAddEmployeeDialog = () => {
    actions.toggleAddEmployeeDialog(true, closeAddEmployeeDialog);
  };

  const closeAddEmployeeDialog = () => {
    actions.toggleAddEmployeeDialog(false);
  };

  const updateSearchTerm = (updatedFilterByName: string) => {
    setFilterByName(updatedFilterByName);
  };

  const handleFilterByEmployeeGroupIdChange = (updatedFilterByGroup: string) => {
    handleFilterChanged({ updatedFilterByGroup });
  };

  const handleFilterByIssuesChange = (updatedFilterByIssue: string) => {
    handleFilterChanged({ updatedFilterByIssue });
  };

  const handleFilterByStatusChange = (updatedFilterByStatus: string) => {
    handleFilterChanged({ updatedFilterByStatus });
  };

  const handleDismissImportEmployeeMessage = () => {
    actions.dismissImportEmployeeListMessage(user);
  };

  const handleManageGroupsClick = () => {
    history.push(employeeGroupsRoute(orgId));
  };

  const handleOnSortChange = (updatedSortBy: string) => {
    if (sortBy !== updatedSortBy) {
      handleFilterChanged({ updatedSortBy, updatedSortDirection: 'asc' });
    }
  };

  const handleOnSortDirectionChange = (updatedSortDirection: string) => {
    if (sortDirection !== updatedSortDirection) {
      handleFilterChanged({ updatedSortDirection });
    }
  };

  // In order to easily share views, filters are added as queryParams and the url is updated on change without page reload
  // However, for text input fields (filterByName) replacing history causes the input to lose focus, so you can only add a letter at a time
  // Instead, we will only update the filterByName param when another filter is updated and changes to filterByName will be stored in state
  const handleFilterChanged = ({
    updatedFilterByIssue,
    updatedFilterByGroup,
    updatedFilterByStatus,
    updatedSortBy,
    updatedSortDirection,
  }: {
    updatedFilterByIssue?: string;
    updatedFilterByGroup?: string;
    updatedFilterByStatus?: string;
    updatedSortBy?: string;
    updatedSortDirection?: string;
  }) => {
    const updatedQueryParams = {
      filterByName,
      filterByIssue: updatedFilterByIssue ?? filterByIssue,
      filterByGroup: updatedFilterByGroup ?? filterByGroup,
      filterByStatus: updatedFilterByStatus ?? filterByStatus,
      sortBy: updatedSortBy ?? sortBy,
      sortDirection: updatedSortDirection ?? sortDirection,
    };
    history.replace(employeesRoute(orgId, updatedQueryParams));
  };

  const changePage = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    page: number,
  ) => {
    setOffset(page * pageSize);
  };

  const filterEmployees = () => {
    let result = employees || [];
    if (filterByIssue) {
      result = issueFilters[filterByIssue as keyof typeof issueFilters].filter(result);
    }
    if (filterByName) {
      const searchName = filterByName.replace(/\s+/g, '').toLowerCase();
      result = result.filter((a) => {
        const name = `${a.Contact.Person.firstName}${a.Contact.Person.lastName}`.toLowerCase();
        return name.includes(searchName) || (a.nickname || '').toLowerCase().includes(searchName);
      });
    }
    if (filterByStatus) {
      result =
        employmentStatusFilters[filterByStatus as keyof typeof employmentStatusFilters].filter(
          result,
        );
    }
    if (filterByGroup) {
      result = result.filter((r) =>
        r.EmployeeGroups.find((g) => g.employeeGroupId.toString() === filterByGroup),
      );
    }
    return result;
  };

  const exportEmployees = async () => {
    const filename = `${org?.org_key || ''}_employees_${moment().format('MM_DD_YY')}.csv`;
    const exportFields = [
      {
        label: 'First Name',
        value: 'Contact.Person.firstName',
      },
      {
        label: 'Nickname',
        value: 'nickname',
      },
      {
        label: 'Last Name',
        value: 'Contact.Person.lastName',
      },
      {
        label: 'Email',
        value: 'Contact.Person.email',
      },
      {
        label: 'Phone Number',
        value: 'phoneNumberString',
      },
      {
        label: 'Birthdate',
        value: 'birthdayString',
      },
      {
        label: 'Language Preference',
        value: 'languagePreferenceString',
      },
      {
        label: 'Job Title',
        value: 'jobTitle',
      },
      {
        label: 'Supervisor',
        value: 'supervisorString',
      },
      {
        label: 'Groups',
        value: 'groupString',
      },
      {
        label: 'Started',
        value: 'startDateString',
      },
      {
        label: 'Application Date',
        value: 'applicationDateString',
      },
      {
        label: 'Employee Identifier',
        value: 'externalId',
      },
    ];
    if (showFormerEmployees) {
      exportFields.push(
        {
          label: 'Active',
          value: 'activeString',
        },
        {
          label: 'End Date',
          value: 'endDateString',
        },
      );
    }
    const exportData = filterEmployees().map((e) => ({
      ...e,
      supervisorString: e.Supervisors.filter((s) => s.isCurrentEmployee)
        .map((s) => employeeName(s))
        .join(', '),
      groupString: e.EmployeeGroups.map((g) => g.name).join(', '),
      startDateString: e.startDate ? moment.utc(e.startDate).format('MM/DD/YYYY') : '',
      birthdayString: e.Contact.Person.birthdate
        ? moment(e.Contact.Person.birthdate).format('MM/DD/YYYY')
        : '',
      phoneNumberString: formatPhoneNumber(e.Contact.Person.phoneNumber),
      applicationDateString: e.JobApplicant?.timestamp
        ? moment(e.JobApplicant.timestamp).format('MM/DD/YYYY hh:mm a')
        : '',
      languagePreferenceString:
        LANGUAGES[e.Contact.Person.languagePreference || ''] || 'English (default)',
      activeString: e.isCurrentEmployee ? 'Yes' : 'No',
      endDateString: e.endDate ? moment.utc(e.endDate).format('MM/DD/YYYY') : '',
      externalId: e.externalId || e.employeeHash,
    }));
    const result = csvExporter(filename, exportFields, exportData);
    if (!result) {
      await error();
    }
  };

  const renderFilters = () => (
    <div className="card u-margin-top u-margin-bottom u-spread">
      <div
        className={`card-body u-display-flex u-alignItems-flexStart ${
          isOnMobile ? 'u-flexWrap-wrap' : ''
        }`}
      >
        <UiFilterTextField label="Name" value={filterByName} onChange={updateSearchTerm} />
        {employeeGroups && employeeGroups.length ? (
          <UiFilterSelect
            label="Group"
            className="u-margin-left"
            value={filterByGroup}
            onChange={handleFilterByEmployeeGroupIdChange}
            options={employeeGroups.map((group) => ({
              label: group.name,
              value: group.employeeGroupId.toString(),
            }))}
          />
        ) : null}
        <UiFilterSelect
          label="Issues"
          className={`u-margin-left ${isOnMobile ? 'u-margin-top-md' : ''}`}
          value={filterByIssue}
          onChange={handleFilterByIssuesChange}
          options={ISSUE_OPTIONS}
        />
        <UiFilterSelect
          label="Status"
          className={`u-margin-left ${isOnMobile ? 'u-margin-top-md' : ''}`}
          value={filterByStatus}
          onChange={handleFilterByStatusChange}
          options={STATUS_OPTIONS}
          hideClear
        />
      </div>
      <div className="u-spread">
        {org?.QuickbooksIntegration && (
          <div
            className="u-padding-y-sm u-padding-x u-margin-right u-spread u-color-white u-background-quickbooks"
            style={{ borderRadius: 30 }}
          >
            <QuickbooksIcon height={15} width={15} className="u-margin-right-sm" />
            Connected to Quickbooks
          </div>
        )}
      </div>
    </div>
  );

  if (employeesAreLoading || !employees || !org) {
    return <LoadingMessage />;
  }

  const hasNoEmployees = employees.length === 0;
  const handleUploadList = () => setUploadDialogOpen(!uploadDialogOpen);

  const renderHeader = () => (
    <UiBreadcrumbHeader
      className="u-position-top-sticky u-background-offWhite"
      pageTitle="Employee Directory"
      primaryActions={[
        {
          name: 'Add Employee',
          handler: openAddEmployeeDialog,
          Icon: <HeaderButtonAddIcon />,
        },
        {
          name: 'Upload Employee List',
          handler: handleUploadList,
        },
      ]}
      secondaryAction={
        hasNoEmployees
          ? undefined
          : {
              name: hasAllEmployeesPermission ? 'Manage Groups' : 'View Groups',
              handler: handleManageGroupsClick,
              variant: 'secondary',
            }
      }
      customSecondaryAction={
        <div className="u-margin-left">
          <PayrollIntegrationButton org={org} size="sm" />
        </div>
      }
      kebabMenu={
        !hasNoEmployees ? (
          <UiKebabMenu menuId="employee-directory-header">
            <MenuItem onClick={exportEmployees}>Export Employees</MenuItem>
          </UiKebabMenu>
        ) : null
      }
    />
  );

  const renderCompactHeader = () => (
    <UiBreadcrumbHeader
      className="u-position-top-sticky u-background-offWhite"
      pageTitle="Employee Directory"
      primaryActions={[
        {
          name: 'Add',
          handler: openAddEmployeeDialog,
          Icon: <HeaderButtonAddIcon />,
        },
        {
          name: 'Upload Employee List',
          handler: handleUploadList,
        },
        hasNoEmployees
          ? undefined
          : {
              name: hasAllEmployeesPermission ? 'Manage Groups' : 'View Groups',
              handler: handleManageGroupsClick,
            },
        hasNoEmployees
          ? undefined
          : {
              name: 'Export Employees',
              handler: exportEmployees,
            },
      ].filter(isTruthy)}
    />
  );

  if (hasNoEmployees) {
    return (
      <div>
        {isOnSmallScreen ? renderCompactHeader() : renderHeader()}
        <div className="card card-body u-margin-lg">
          <EmployeeEmptyState orgId={orgId} listUploadOnly />
        </div>
        {uploadDialogOpen && <EmployeeListUploadDialog org={org} closeDialog={handleUploadList} />}
      </div>
    );
  }

  const activeEmployees = employees.filter((e) => e.isCurrentEmployee);

  const hasNotAddedEmployees = orgHasNotAddedEmployees(employees);
  const currentDirectoryIssues: string[] = [];
  if (!hasNotAddedEmployees) {
    const halfActiveEmployees = activeEmployees.length / 2;
    possibleIssues.forEach((issue) => {
      const employeesWithIssues = issueFilters[issue].filter(activeEmployees);
      if (employeesWithIssues.length > halfActiveEmployees) {
        currentDirectoryIssues.push(issueFilters[issue].message);
      }
      return null;
    });
  }
  const filteredEmployees = filterEmployees().sort(
    sortAlphabetically(
      (e) => {
        if (sortBy === 'firstName') {
          return e.nickname ? e.nickname : e.Contact.Person.firstName;
        }
        if (sortBy === 'status') {
          return employeeStatusText(e);
        }
        return e.Contact.Person[sortBy];
      },
      sortDirection === 'asc' ? SORT_ORDER.ASC : SORT_ORDER.DESC,
    ),
  );
  const paginatedEmployees =
    filteredEmployees.length > pageSize
      ? filteredEmployees.slice(offset, offset + pageSize)
      : filteredEmployees;
  const hasAllEmployeesPermission = user.employeesPermission === EMPLOYEE_PERMISSION.ALL;

  const hiddenEmployees =
    (showFormerEmployees ? employees.length : activeEmployees.length) - filteredEmployees.length;

  const missingPhoneNumberEmployees = employees.filter(
    (e) => !e.Contact.Person.phoneNumber && e.Contact.Person.email,
  ).length;
  const missingContactInfoEmployees = issueFilters.missingContactInfo.filter(employees).length;
  const missingPhoneNumberMessage = `${
    missingPhoneNumberEmployees
      ? `${missingPhoneNumberEmployees} employee${
          missingPhoneNumberEmployees === 1 ? '' : 's'
        } receive${
          missingPhoneNumberEmployees === 1 ? 's' : ''
        } messages as emails instead of texts`
      : ''
  }`;
  const missingContactInfoMessage = `${
    missingContactInfoEmployees
      ? `${missingContactInfoEmployees} ${
          missingContactInfoEmployees === 1 ? 'has' : 'have'
        } no contact information`
      : ''
  }`;
  const contactIssuesMessage = [missingPhoneNumberMessage, missingContactInfoMessage]
    .filter(isTruthy)
    .join(', ');

  const handleViewContactIssues = () => {
    handleFilterByIssuesChange('missingPhoneNumber');
  };

  return (
    <div>
      {isOnSmallScreen ? renderCompactHeader() : renderHeader()}
      <div className="u-margin-lg">
        <FeatureFlagged
          variant={FeatureGatedVariant.HIDE_ON_FLAG}
          flagName={FeatureGates.INTEGRATED_MESSAGING}
        >
          <div className="u-display-flex u-flexDirection-row u-justifyContent-spaceBetween u-alignItems-center">
            <Hidden xsDown>
              <Typography variant="caption">
                Employee Phone Number <br />
                {formatPhoneNumber(org.phoneNumber)}
              </Typography>
            </Hidden>
          </div>
        </FeatureFlagged>
        <FeatureFlagged
          variant={FeatureGatedVariant.SHOW_ON_FLAG}
          flagName={FeatureGates.INTEGRATED_MESSAGING}
        >
          {contactIssuesMessage && (
            <UiMessageBanner
              message={contactIssuesMessage}
              action={{ name: 'View List', handler: handleViewContactIssues }}
              variant="attention"
            />
          )}
        </FeatureFlagged>
        {!hasDismissedImportEmployeeListMessage && currentDirectoryIssues.length > 0 && (
          <div className="u-margin-y">
            <Message variant="warning" onMessageDismissed={handleDismissImportEmployeeMessage}>
              More than half of your employees have {combineWords(currentDirectoryIssues, 'or')}. To
              fully utilize employee automations, consider uploading an employee list to add the
              missing information.
              <a
                className="typo-link"
                href="https://help.teamengine.io/article/44-set-up-your-employee-directory"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span> Learn more</span>
              </a>
            </Message>
          </div>
        )}
        {renderFilters()}
        {!filterByName && (filterByGroup || filterByIssue) && (
          <div className="u-margin-bottom typo-label">
            Hiding {hiddenEmployees} {hiddenEmployees === 1 ? 'employee' : 'employees'}
          </div>
        )}
        {filteredEmployees.length === 0 ? (
          <div className="card">
            <div className="card-body">
              <div className="typo-h6 u-textAlign-center">
                No employees found matching your filters
              </div>
            </div>
          </div>
        ) : (
          <div className="card">
            <Hidden smDown>
              <div className="u-padding-lg">
                <Grid container spacing={2} alignContent="center" alignItems="center">
                  <Grid item md={hasAllEmployeesPermission ? 2 : 3}>
                    <UiSortLabel
                      label="First Name"
                      sortKey="firstName"
                      currentSortBy={sortBy}
                      currentSortDirection={sortDirection}
                      onSortByChange={handleOnSortChange}
                      onSortDirectionChange={handleOnSortDirectionChange}
                    />
                  </Grid>
                  <Grid item md={hasAllEmployeesPermission ? 2 : 3}>
                    <UiSortLabel
                      label="Last Name"
                      sortKey="lastName"
                      currentSortBy={sortBy}
                      currentSortDirection={sortDirection}
                      onSortByChange={handleOnSortChange}
                      onSortDirectionChange={handleOnSortDirectionChange}
                    />
                  </Grid>
                  <Grid item md={2}>
                    Groups
                  </Grid>
                  {hasAllEmployeesPermission && (
                    <Grid item md={2}>
                      Supervisors
                    </Grid>
                  )}
                  <UiSortLabel
                    label="Status"
                    sortKey="status"
                    currentSortBy={sortBy}
                    currentSortDirection={sortDirection}
                    onSortByChange={handleOnSortChange}
                    onSortDirectionChange={handleOnSortDirectionChange}
                  />
                  <Grid item md={hasAllEmployeesPermission ? 2 : 3} />
                </Grid>
              </div>
            </Hidden>
            {paginatedEmployees.map((e) => (
              <EmployeeSummaryCard
                key={`employee-directory-${e.employeeId}`}
                employee={e}
                mergeIntegration={mergeIntegration}
                hrisIntegrationResponse={hrisIntegrationResponse}
                org={org}
                hasAllEmployeesPermission={hasAllEmployeesPermission}
                viewDetail={viewDetail(e.employeeId, 'DETAILS')}
                viewNotes={viewDetail(e.employeeId, 'COMMENTS')}
                message={message(e.employeeId)}
              />
            ))}
          </div>
        )}
        {filteredEmployees.length >= pageSize && (
          <TablePagination
            component="div"
            count={filteredEmployees.length}
            rowsPerPage={pageSize}
            page={offset / pageSize}
            backIconButtonProps={{
              'aria-label': 'Previous Page',
            }}
            nextIconButtonProps={{
              'aria-label': 'Next Page',
            }}
            onPageChange={changePage}
            rowsPerPageOptions={[pageSize]}
          />
        )}
        {/* React's preferred solution for not using derived state (reason why I have a key props below)
        https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#preferred-solutions */}
        {viewEmployeeDetailId && (
          <EmployeeProfileDialog
            key={viewEmployeeDetailId}
            orgId={org.org_id}
            employeeId={viewEmployeeDetailId}
            onClose={closeDetailsDialog}
            tab={selectedTab}
          />
        )}
        {uploadDialogOpen && <EmployeeListUploadDialog org={org} closeDialog={handleUploadList} />}
      </div>
    </div>
  );
});
